import React from "react";
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { trimString } from "util/stringUtils";
import BlueInfoTooltip from "visual-components/util/tooltip/BlueInfoTooltip";

/**
 * Analytics section header component with optional tooltip and dropdown
 * @param {Object} props
 * @param {string} props.title - The header title text
 * @param {string} [props.tooltipText] - Optional tooltip text to display
 * @param {import("@mui/material").TypographyVariant} [props.variant] - Typography variant (default: "h4")
 * @param {boolean} [props.showSelect=false] - Whether to show the dropdown
 * @param {Object} [props.selectProps] - Props for the Select component
 * @param {Array<{value: string, label: string}>} [props.selectProps.options] - Dropdown options
 * @param {string} [props.selectProps.value] - Selected dropdown value
 * @param {Function} [props.selectProps.onChange] - Dropdown change handler
 * @returns {import("react").ReactElement}
 */
export default function AnalyticsHeader(props) {
  const {
    title,
    tooltipText = null,
    selectProps = {
      options: [],
      value: "",
      onChange: () => {},
    },
    showSelect = false,
    variant = "h4",
  } = props;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography color="primary" variant={variant}>
        {title}
        {showSelect ? " - " : ""}
      </Typography>
      {showSelect && (
        <FormControl sx={{ minWidth: 120, ml: 1 }}>
          <Select
            value={selectProps.value}
            onChange={(e) => selectProps.onChange(e.target.value)}
            displayEmpty
            size="small"
          >
            {selectProps.options.map((option) => (
              <MenuItem key={option.value ?? "all"} value={option.value}>
                {trimString(option.label, 30)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {tooltipText && <BlueInfoTooltip text={tooltipText} />}
    </Box>
  );
}
